import {
  Component,
  OnInit,
  OnDestroy,
  DoCheck,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewContainerRef,
  HostListener,
  NgZone,
  Injector,
  effect,
  untracked,
  Signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { delay } from "rxjs/operators";
import {
  IClippingStudioRepository,
} from "app/js/src/domain/clipping-studio/i-clipping-studio-repository";
import {
  ClippingStudioManagerService,
  ClippingStudioProjectSaveStatus,
} from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { ClippingStudioState } from "app/js/src/domain/clipping-studio/clipping-studio-state";
import { ClippingStudioEditorMode } from "app/js/src/domain/clipping-studio/clipping-studio-editor-mode";
import { ClippingStudioTimelineComponent } from "./timeline/clipping-studio-timeline.component";
import { ClippingStudioVideoPreviewComponent } from "./video-preview/clipping-studio-video-preview.component";
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";
import { EvrideoKeyboardShortcutsComponent } from "../common/evrideo-keyboard-shortcuts/evrideo-keyboard-shortcuts.component";
import { ClippingStudioAutoReframeModalComponent } from "./auto-reframe-modal/clipping-studio-auto-reframe-modal.component";
import { ClippingStudioKeyboardShortcuts } from "app/js/src/domain/common/keyboard-shortcuts";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import { ClippingStudioMamItem } from "app/js/src/domain/clipping-studio/clipping-studio-mam-item";
import {
  ClippingStudioTextEvent,
  ClippingStudioGraphicEvent,
  ReframeConfig,
  ReframeKeyframe,
  getReframeConfigAtTime,
  AppendedMamSegment,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  SocialAccount,
  DestinationType,
} from "app/js/src/domain/clipping-studio/publish/publish-models";
import { ClippingStudioGfxManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-gfx-manager.service";
import {
  AddGfxToTimelineRequest,
  UpdateTimelineGfxParamsRequest,
} from "./panels/graphics-panel/clipping-studio-graphics-panel.component";
import { ToastrService } from "ngx-toastr";
import {
  ClipPreviewAspectRatio,
} from "app/js/src/domain/clipping-studio/clip-preview-model";
import {
  captureTranscodedFrameDataUrl,
  resolveTranscodedSourceAtTime,
  withEphemeralTranscodedVideo,
} from "app/js/src/infrastructure/clipping-studio/clipping-studio-transcoded-frame-capture";
import {
  CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
  CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
} from "app/js/src/domain/clipping-studio/clip-preview-image-encode";

@Component({
  selector: "evrideo-clipping-studio",
  templateUrl: "./clipping-studio.component.html",
  styleUrls: ["./clipping-studio.component.scss"],
})
export class ClippingStudioComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChild(ClippingStudioVideoPreviewComponent)
  videoPreviewRef: ClippingStudioVideoPreviewComponent | null = null;
  @ViewChild(ClippingStudioTimelineComponent)
  timelineRef: ClippingStudioTimelineComponent;
  @ViewChild("rewindWrap") rewindWrapRef: ElementRef<HTMLElement> | null = null;

  state: ClippingStudioState;

  /** Rewind context menu (jump back minutes) open state */
  rewindMenuOpen = false;

  /** Effective text/graphics for the current preview destination (base + overrides + destination-only). */
  effectiveText: ClippingStudioTextEvent[] = [];
  effectiveGraphics: ClippingStudioGraphicEvent[] = [];

  /** Local UI state: captions on/off, annotation toolbar visible, audio muted */
  captionsOn = false;
  showAnnotationToolbar = false;
  isFitSegment = false;
  /** When set (SOURCE + MAM), timeline shows only this segment; zoom/GoTo In/Out apply to it. null = show all sources (fit view). */
  zoomedSourceSegmentIndex: number | null = null;
  /** When true (SOURCE + MAM), the zoomed segment is shown in fit mode (no scroll); first Zoom In sets this. Second Zoom In clears it and applies real zoom. */
  zoomedSegmentFit = false;
  audioMuted = false;

  /** Playback speed (1 = normal). */
  playbackRate = 1;

  /** Sidebar visibility: when false, show thin strip with expand button */
  showLeftSidebar = true;
  showRightSidebar = true;

  /** Left sidebar active tool panel (ai, media, text, etc.). Controlled so text selection can open to Text. */
  leftSidebarActiveTool = "ai";

  /** Top bar: editable title */
  isEditingTitle = false;
  editedTitle = "";

  /** Top bar: project autosave indicator (saved / saving / error). */
  projectSaveStatus: ClippingStudioProjectSaveStatus = "saved";

  /** Top bar: destination selected for preview (dropdown). null = Original. */
  selectedPreviewDestinationId: string | null = null;

  /** Selected destinations (checked in right sidebar) for the aspect-ratio dropdown. */
  selectedDestinationsForDropdown: SocialAccount[] = [];

  /** Timeline zoom (same model as content-control-studio): block duration in seconds. */
  availableTimelineZoomSteps = [
    1, 2, 3, 4, 5, 10, 30, 60, 300, 600, 900, 1800, 3600, 7200,
  ];
  timelineZoomSteps: number[] = [];
  selectedTimelineZoomStepIndex = 0;
  selectedTimelineBlockDuration = 600;

  /** True while a MAM segment/clip is being added and its video is loading; blocks timeline and keyboard. */
  isTimelineMediaLoading = false;

  /** True while a Publish (Exit/Continue) save+submit is in flight; blocks duplicate publish clicks and keyboard. */
  isPublishing = false;

  private static readonly LOAD_TIMEOUT_MS = 30_000;

  /** Parses GFX `Duration` query param (ms); matches add-GFX default when missing or invalid. */
  private static resolveGfxDurationMs(gfxParams: Record<string, string>): number {
    const raw = gfxParams["Duration"];
    if (raw === undefined || raw === "") return 10_000;
    const parsed = parseFloat(String(raw));
    if (!Number.isFinite(parsed) || parsed <= 0) return 10_000;
    return Math.round(parsed);
  }

  /** Last effective timeline duration (sec) used for zoom; recalc when mode/clips change. */
  private lastEffectiveDurationSec = 0;
  private subscriptions: Subscription[] = [];

  /** Current publication uid from route (studio entry gate). */
  publicationUid: string | null = null;

  /** Route params as a signal; effect in constructor calls tryLoadFromRoute when params change. */
  private routeParams!: Signal<ParamMap | undefined>;

  /** Full-frame Auto-Reframe modal: when true, overlay is shown with modal content. */
  showAutoReframeModal = false;
  /** Context passed to the Auto-Reframe modal (set when opening). */
  autoReframeContext: {
    clips: ClippingStudioClip[];
    destinationId: string;
    aspectRatio: AspectRatio;
    media: { previewUrl: string; fps?: number; durationMs?: number; focusUrl?: string };
    initialReframeKeyframes: Record<string, ReframeKeyframe[]>;
    mainDurationMs: number;
    appendedMamSegments: AppendedMamSegment[];
    editMamClips: { id: string; inMs: number; outMs: number; previewUrl: string }[];
  } | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private repository: IClippingStudioRepository,
    public manager: ClippingStudioManagerService,
    public publishState: PublishStateService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private _dialogService: IEvrideoDialogService,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone,
    injector: Injector,
    public readonly gfxManager: ClippingStudioGfxManagerService,
  ) {
    this.routeParams = toSignal(this.route.paramMap, { injector });
    effect(() => {
      const params = this.routeParams();
      if (params) {
        untracked(() => this.tryLoadFromRoute());
      }
    });
  }

  /** Destinations from publish state (for other use). Top-bar dropdown uses selectedDestinationsForDropdown. */
  get destinations() {
    return this.publishState.getDestinations();
  }

  /** Tooltip for the top-bar project save status icon. */
  get projectSaveStatusTitle(): string {
    switch (this.projectSaveStatus) {
      case "saving":
        return "Saving changes...";
      case "error":
        return "Saving failed!";
      case "saved":
      default:
        return "Project saved";
    }
  }

  /** Short label for destination type in dropdown (e.g. YT, TTV, FB). */
  getDestinationShortLabel(d: SocialAccount): string {
    const map: Record<DestinationType, string> = {
      yt: "YT",
      fb: "FB",
      ttv: "TTV",
      ig: "IG",
      s3: "S3",
      tt: "TT",
    };
    return map[d.type] ?? d.platform.slice(0, 2).toUpperCase();
  }

  /** Label for one aspect-ratio option: e.g. "9:16 (YT)". */
  getPreviewOptionLabel(d: SocialAccount): string {
    const ratio = this.publishState.getConfig(d.id).aspectRatio;
    const short = this.getDestinationShortLabel(d);
    return `${ratio} (${short})`;
  }

  /** True when the user can switch to EDIT mode (at least one clip must exist). */
  get canSwitchToEdit(): boolean {
    return (this.state?.clips?.length ?? 0) > 0;
  }

  /** Aspect ratio for the studio preview: in SOURCE mode always 16:9 (Original); in EDIT from selected destination or 16:9. */
  get previewAspectRatio(): AspectRatio {
    if (this.state?.mode === "SOURCE") return AspectRatio.Aspect16x9;
    return this.publishState.getPreviewAspectRatio();
  }

  /** Current segment reframe config when in edit mode with a non-original destination; null otherwise. Resolved from keyframes at current time. */
  get currentSegmentReframeConfig(): ReframeConfig | null {
    if (this.state?.mode !== "EDIT" || this.selectedPreviewDestinationId == null) return null;
    const destId = this.selectedPreviewDestinationId;
    const variants = this.state.destinationVariants?.[destId];
    if (!variants?.segmentReframes) return null;
    const clip = this.manager.getClipAtPlayhead();
    if (!clip) return null;
    const t = this.manager.getCurrentSourceTimeMs();
    const keyframes = variants.segmentReframes[clip.id] ?? [];
    return getReframeConfigAtTime(keyframes, t - clip.inMs);
  }

  onPreviewDestinationChange(id: string | null): void {
    const value = id === "" || id == null ? null : id;
    this.selectedPreviewDestinationId = value;
    this.manager.setCurrentPreviewDestinationId(value);
    this.publishState.setSelectedPreviewDestinationId(value);
  }

  /**
   * GFX iframe lifecycle must NOT depend on the currently selected preview destination.
   *
   * We keep iframes alive as long as the timeline GFX segment exists in at least one context:
   * - base/original graphics (`state.graphics`)
   * - any destination-only graphics (`destinationVariants[*].graphicOnly`)
   *
   * Per-destination visibility remains controlled by `effectiveGraphics`.
   */
  private getAllGfxSegmentsPresentInAnyDestination(
    state: ClippingStudioState,
  ): ClippingStudioGraphicEvent[] {
    const result: ClippingStudioGraphicEvent[] = [];
    const seen = new Set<string>();

    const add = (g: ClippingStudioGraphicEvent): void => {
      if (g.type !== "gfx" || g.gfxInstanceId == null) return;
      if (seen.has(g.id)) return;
      seen.add(g.id);
      result.push(g);
    };

    for (const g of state.graphics ?? []) {
      add(g);
    }

    const variants = state.destinationVariants;
    if (variants) {
      for (const v of Object.values(variants)) {
        for (const g of v?.graphicOnly ?? []) {
          add(g);
        }
      }
    }

    return result;
  }

  /** True when zoom is at the widest (fit) level – timeline fits the container. Includes "zoomed segment fit" (one segment in fit). */
  get isVideoTimelineFit(): boolean {
    return (
      this.timelineZoomSteps.length - 1 === this.selectedTimelineZoomStepIndex ||
      (this.state?.mode === "SOURCE" &&
        (this.state?.appendedMamSegments?.length ?? 0) > 0 &&
        this.zoomedSourceSegmentIndex != null &&
        this.zoomedSegmentFit)
    );
  }

  /** Effective timeline duration in seconds: in EDIT mode = sum of clip lengths; in SOURCE with MAM = main + appended, or single segment when zoomed into one. */
  private getEffectiveTimelineDurationSec(
    s: ClippingStudioState | null,
  ): number {
    if (!s) return 0;
    if (s.mode === "EDIT" && s.clips?.length) {
      const ordered = [...s.clips].sort((a, b) => a.order - b.order);
      return ordered.reduce((sum, c) => sum + (c.outMs - c.inMs) / 1000, 0);
    }
    if (s.mode === "SOURCE" && (s.appendedMamSegments?.length ?? 0) > 0) {
      if (this.zoomedSourceSegmentIndex != null) {
        const mainSec = (s.media?.durationMs ?? 0) / 1000;
        const segmentDurationsSec: number[] = [mainSec];
        for (const seg of s.appendedMamSegments ?? []) {
          segmentDurationsSec.push(seg.durationMs / 1000);
        }
        const idx = Math.max(
          0,
          Math.min(this.zoomedSourceSegmentIndex, segmentDurationsSec.length - 1),
        );
        return segmentDurationsSec[idx];
      }
      const main = s.media?.durationMs ?? 0;
      const appendedTotal = (s.appendedMamSegments ?? []).reduce(
        (sum, seg) => sum + seg.durationMs,
        0,
      );
      return (main + appendedTotal) / 1000;
    }
    return s.media?.durationMs != null ? s.media.durationMs / 1000 : 0;
  }

  /** Edit / stitched timeline length (ms); used to clamp GFX segment end. Mirrors timeline `durationMs`. */
  private getEditTimelineDurationMs(): number {
    const s = this.state;
    if (!s) return 0;
    if (s.mode === "EDIT" && s.clips?.length) {
      const ordered = [...s.clips].sort((a, b) => a.order - b.order);
      const sum = ordered.reduce((acc, c) => acc + (c.outMs - c.inMs), 0);
      if (sum > 0) return sum;
    }
    const main = s.media?.durationMs ?? 0;
    const appended = s.appendedMamSegments ?? [];
    const appendedTotal = appended.reduce((acc, seg) => acc + seg.durationMs, 0);
    return main + appendedTotal;
  }

  /** Segment index (0 = main, 1+ = MAM) containing the current playhead. Only valid when SOURCE with MAM. */
  private getSegmentIndexAtPlayhead(): number {
    const s = this.state;
    if (!s || s.mode !== "SOURCE" || (s.appendedMamSegments?.length ?? 0) === 0)
      return 0;
    const mainMs = s.media?.durationMs ?? 0;
    const t = this.manager.getDisplayTimeMs();
    if (t < mainMs) return 0;
    let cursorMs = mainMs;
    for (let i = 0; i < (s.appendedMamSegments ?? []).length; i++) {
      cursorMs += s.appendedMamSegments![i].durationMs;
      if (t < cursorMs) return i + 1;
    }
    return (s.appendedMamSegments ?? []).length;
  }

  /** Start time (ms) of segment at index in global timeline. SOURCE + MAM only. */
  getSegmentStartMs(segmentIndex: number): number {
    const s = this.state;
    if (!s || (s.appendedMamSegments?.length ?? 0) === 0) return 0;
    if (segmentIndex <= 0) return 0;
    const mainMs = s.media?.durationMs ?? 0;
    if (segmentIndex === 1) return mainMs;
    let cursor = mainMs;
    for (let i = 0; i < segmentIndex - 1 && i < (s.appendedMamSegments ?? []).length; i++) {
      cursor += s.appendedMamSegments![i].durationMs;
    }
    return cursor;
  }

  /** End time (ms) of segment at index in global timeline. SOURCE + MAM only. */
  getSegmentEndMs(segmentIndex: number): number {
    const s = this.state;
    if (!s || (s.appendedMamSegments?.length ?? 0) === 0)
      return s?.media?.durationMs ?? 0;
    const mainMs = s.media?.durationMs ?? 0;
    const segmentDurationsMs: number[] = [mainMs];
    for (const seg of s.appendedMamSegments ?? []) {
      segmentDurationsMs.push(seg.durationMs);
    }
    const idx = Math.min(segmentIndex, segmentDurationsMs.length - 1);
    let end = 0;
    for (let i = 0; i <= idx; i++) end += segmentDurationsMs[i];
    return end;
  }

  /** Clip at current playhead (EDIT mode with clips); null otherwise. */
  get currentClipAtPlayhead(): ClippingStudioClip | null {
    if (
      this.state?.mode === "EDIT" &&
      this.state.clips &&
      this.state.clips.length > 0
    ) {
      return this.manager.getClipAtPlayhead();
    }
    return null;
  }

  /** Subtitles to pass to video preview: when playhead is on a MAM clip, only user-added (so main-source transcript subs are not shown on MAM). */
  get effectiveSubtitlesForPreview(): ClippingStudioState["subtitles"] {
    const clip = this.currentClipAtPlayhead;
    if (clip?.mamMediaId != null) {
      return (this.state?.subtitles ?? []).filter((s) => s.fromMainSource === false);
    }
    return this.state?.subtitles ?? [];
  }

  ngOnInit(): void {
    this.selectedPreviewDestinationId = this.publishState.selectedPreviewDestinationId;
    this.manager.setCurrentPreviewDestinationId(this.selectedPreviewDestinationId);
    this.manager.setClipPreviewFrameProvider((timeMs, options) =>
      this.captureClipPreviewFrameForProvider(timeMs, options),
    );
    this.syncZoomedSegmentBoundsToManager();
    this.subscriptions.push(
      this.manager.state$.subscribe((s) => {
        this.state = s;
        this.effectiveText = this.manager.getEffectiveText(s, this.selectedPreviewDestinationId);
        this.effectiveGraphics = this.manager.getEffectiveGraphics(s, this.selectedPreviewDestinationId);
        this.gfxManager.syncFromProject(this.getAllGfxSegmentsPresentInAnyDestination(s));
        const effectiveSec = this.getEffectiveTimelineDurationSec(s);
        if (
          effectiveSec > 0 &&
          (this.timelineZoomSteps.length === 0 ||
            this.lastEffectiveDurationSec !== effectiveSec)
        ) {
          this.lastEffectiveDurationSec = effectiveSec;
          this.calculateZoomLevels(effectiveSec);
        }
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      this.manager.saveStatus$.subscribe((status) => {
        this.projectSaveStatus = status;
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      this.publishState.selectedPreviewDestinationId$.subscribe((id) => {
        this.selectedPreviewDestinationId = id;
        this.manager.setCurrentPreviewDestinationId(id);
        if (this.state) {
          this.effectiveText = this.manager.getEffectiveText(this.state, id);
          this.effectiveGraphics = this.manager.getEffectiveGraphics(this.state, id);
          this.gfxManager.syncFromProject(this.getAllGfxSegmentsPresentInAnyDestination(this.state));
        }
        this.manager.selectText(null);
        this.manager.selectGraphic(null);
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      this.publishState.selectedDestinations$.subscribe((ids) => {
        const all = this.publishState.getDestinations();
        this.selectedDestinationsForDropdown = all.filter((d) =>
          ids.includes(d.id),
        );
        if (
          this.selectedPreviewDestinationId &&
          !ids.includes(this.selectedPreviewDestinationId)
        ) {
          this.selectedPreviewDestinationId = null;
          this.manager.setCurrentPreviewDestinationId(null);
          this.publishState.setSelectedPreviewDestinationId(null);
        }
        this.cdr.detectChanges();
      }),
    );
    this.subscriptions.push(
      this.manager.clipPreviewSnapshotRequest$.subscribe((req) => this.onSnapshot(req)),
    );
    this.subscriptions.push(
      this.manager.initialClipPreviewSnapshotRequest$
        .pipe(delay(500))
        .subscribe(() => this.onSnapshot()),
    );
    this.subscriptions.push(
      this.manager.segmentAddedFromPanel$.subscribe((clipId) =>
        this.onSegmentAddedFromPanel(clipId),
      ),
    );
    const updateDestAspectRatios = (): void => {
      const dests = this.publishState.getDestinations();
      const map: Record<string, AspectRatio> = {};
      dests.forEach((d) => {
        map[d.id] = this.publishState.getConfig(d.id).aspectRatio;
      });
      this.manager.setDestinationAspectRatios(map);
    };
    updateDestAspectRatios();
    this.subscriptions.push(
      this.publishState.configs$.subscribe(() => updateDestAspectRatios()),
    );
  }

  ngDoCheck(): void {
    this.syncZoomedSegmentBoundsToManager();
  }

  /** Sync zoomed segment to manager so seek and playback stay within segment when zoomed in (SOURCE, not FIT). Manager computes bounds from its own state. */
  private syncZoomedSegmentBoundsToManager(): void {
    if (
      this.state?.mode === "SOURCE" &&
      (this.state.appendedMamSegments?.length ?? 0) > 0
    ) {
      this.manager.setZoomedSegment(
        this.zoomedSourceSegmentIndex,
        this.zoomedSegmentFit,
      );
    } else {
      this.manager.setZoomedSegment(null, true);
    }
  }

  /**
   * Load studio via GET /api/publications/{publicationUid}/open.
   */
  private tryLoadFromRoute(): void {
    const publicationUid = this.getPublicationUidParam();
    if (!publicationUid) return;

    // Avoid duplicate loads when route param effect re-runs.
    if (this.publicationUid === publicationUid && this.manager.state.projectId != null) {
      return;
    }

    this.publicationUid = publicationUid;
    void this.repository
      .openProject(publicationUid)
      .then((res) => {
        this.publishState.hydrateFromProjectPublishFields(
          res.project.publishMetadataJson,
          res.project.publishMetadataOverridesJson,
        );
        return this.manager.openProject(res.projectId, res.project);
      })
      .catch((err) => {
        console.error("Failed to open Clipping Studio publication", err);
        const status = (err as any)?.status;
        if (status === 403) {
          this.toastr.error("This publication cannot be opened for editing.");
          return;
        }
        if (status === 404) {
          this.toastr.error("Publication not found.");
          return;
        }
        this.toastr.error("Could not open Clipping Studio. Please try again.");
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.manager.setClipPreviewFrameProvider(null);
    this.manager.clear();
  }

  onModeChange(mode: ClippingStudioEditorMode): void {
    if (mode === "EDIT" && !this.canSwitchToEdit) return;
    this.manager.setMode(mode);
  }

  onTextPositionChange(p: { id: string; x: number; y: number; isDragging?: boolean }): void {
    this.manager.updateText(p.id, { x: p.x, y: p.y }, { skipUndo: p.isDragging, skipPersist: p.isDragging });
  }

  onTextDragStart(): void {
    this.manager.beginTextMove();
  }

  onTextDragEnd(): void {
    this.manager.endTextDrag();
  }

  onTextSelected(id: string): void {
    this.manager.selectText(id);
    this.showLeftSidebar = true;
    this.leftSidebarActiveTool = "text";
  }

  onGraphicSelected(id: string): void {
    this.manager.selectGraphic(id);
    this.showLeftSidebar = true;
    this.leftSidebarActiveTool = "graphics";
  }

  onSubtitleSelected(id: string): void {
    this.manager.setSelectedSubtitleId(id);
    this.showLeftSidebar = true;
    this.leftSidebarActiveTool = "transcript";
  }

  onGraphicPositionChange(p: { id: string; x: number; y: number; isDragging?: boolean }): void {
    this.manager.updateGraphic(p.id, { x: p.x, y: p.y }, {
      skipUndo: p.isDragging,
      skipPersist: p.isDragging,
    });
  }

  onGraphicDragEnd(): void {
    this.manager.endGraphicDrag();
  }

  onGraphicDragStart(): void {
    this.manager.beginGraphicMove();
  }

  onTitleClick(): void {
    this.isEditingTitle = true;
    this.editedTitle = this.state?.projectTitle ?? "";
  }

  onTitleBlur(): void {
    this.isEditingTitle = false;
    const trimmed = this.editedTitle?.trim();
    if (
      trimmed !== undefined &&
      trimmed !== "" &&
      this.manager.state.projectTitle !== trimmed
    ) {
      this.manager.setProjectTitle(trimmed);
    }
  }

  onTitleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.onTitleBlur();
    } else if (event.key === "Escape") {
      this.isEditingTitle = false;
      this.editedTitle = this.state?.projectTitle ?? "";
    }
  }

  /** When reframe modal is open, returns current state clips (sorted) so thumbnails update live. */
  get reframeModalClips(): ClippingStudioClip[] {
    if (!this.showAutoReframeModal || !this.state?.clips?.length) return [];
    return [...this.state.clips].sort((a, b) => a.order - b.order);
  }

  openAutoReframeDialog(): void {
    const destId = this.selectedPreviewDestinationId;
    if (destId == null || !this.state?.clips?.length || !this.state.media?.previewUrl) return;
    const clips = [...this.state.clips].sort((a, b) => a.order - b.order);
    const aspectRatio = this.publishState.getConfig(destId).aspectRatio;
    const media = {
      previewUrl: this.state.media.previewUrl,
      fps: this.state.media.fps,
      durationMs: this.state.media.durationMs,
      focusUrl: this.state.media.focusUrl,
    };
    const initialReframeKeyframes =
      this.state.destinationVariants?.[destId]?.segmentReframes ?? {};
    const editMamClips = (this.state.clips ?? [])
      .filter((c) => c.mamMediaId != null && c.previewUrl != null && c.previewUrl !== "")
      .sort((a, b) => a.order - b.order)
      .map((c) => ({ id: c.id, inMs: c.inMs, outMs: c.outMs, previewUrl: c.previewUrl! }));
    this.autoReframeContext = {
      clips,
      destinationId: destId,
      aspectRatio,
      media,
      initialReframeKeyframes: { ...initialReframeKeyframes },
      mainDurationMs: this.state.media?.durationMs ?? 0,
      appendedMamSegments: this.state.appendedMamSegments ?? [],
      editMamClips,
    };
    this.showAutoReframeModal = true;
    this.cdr.detectChanges();
  }

  onAutoReframeSave(segmentReframeKeyframes: Record<string, ReframeKeyframe[]>): void {
    if (this.autoReframeContext) {
      this.manager.setSegmentReframesForDestination(
        this.autoReframeContext.destinationId,
        segmentReframeKeyframes,
      );
    }
  }

  onAutoReframeClosed(): void {
    this.showAutoReframeModal = false;
    this.autoReframeContext = null;
    this.cdr.detectChanges();
  }

  openKeyboardShortcutsDialog(): void {
    this._dialogService
      .openDialog(this.viewContainerRef, {
        type: "simple",
        title: "Keyboard Shortcuts",
        content: EvrideoKeyboardShortcutsComponent,
        contentContext: {
          shortcuts: ClippingStudioKeyboardShortcuts,
        },
        timeout: -1,
      })
      .finally(() => {});
  }

  /**
   * Adds MAM media to the timeline (append segment in Source mode, add clip in Edit mode).
   * Shows loading dialog, blocks timeline and keyboard until the video has loaded. On timeout/error removes the added item.
   */
  addMamToTimeline(item: ClippingStudioMamItem): void {
    if (this.isTimelineMediaLoading) return;
    if (!item?.mamMediaId || (item.durationMs ?? 0) <= 0) return;
    if (!this.videoPreviewRef) return;

    this.isTimelineMediaLoading = true;
    this.cdr.markForCheck();

    let added: AppendedMamSegment | ClippingStudioClip | void | undefined;
    const loadPromise = (async () => {
      if (this.state?.mode === "SOURCE") {
        added = await this.manager.addAppendedMamSegment(
          item.mamMediaId,
          item.durationMs!,
          item.title ?? undefined,
        );
      } else {
        added = await this.manager.addMamClipInEditMode(
          item.mamMediaId,
          item.durationMs!,
          item.title ?? undefined,
        );
      }
      if (!added) throw new Error("Add failed");
      await this.videoPreviewRef.whenVideoReady(
        added.id,
        ClippingStudioComponent.LOAD_TIMEOUT_MS,
      );
    })().catch((err: Error) => {
      if (added) {
        if ("order" in added) {
          this.manager.removeClip(added.id);
        } else {
          this.manager.removeAppendedMamSegmentBySegmentId(added.id);
        }
      }
      throw err;
    });

    this._dialogService
      .openDialogWithLoading(
        this.viewContainerRef,
        {
          title: "Adding media",
          loadingContent: "Adding media to timeline...",
        },
        loadPromise,
        () => ({
          type: "success",
          title: "Done",
          content: "Media added.",
          timeout: 3000,
          hideCloseButton: true,
        }),
        (err) => ({
          type: "error",
          title: "Error",
          content: err?.message ?? "Media failed to load",
          hideCloseButton: false,
        }),
      )
      .finally(() => {
        this.isTimelineMediaLoading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Adds a GFX graphic to the timeline with a blocking loading modal.
   * Materializes the GFX, creates the iframe, waits for `gfx-loaded`,
   * and only then adds the event to project state. On failure the iframe
   * and runtime entry are torn down and the modal shows the error.
   */
  onAddGfxToTimeline(request: AddGfxToTimelineRequest): void {
    if (this.isTimelineMediaLoading) return;
    if (this.state?.mode !== "EDIT") return;

    this.isTimelineMediaLoading = true;
    this.cdr.markForCheck();

    // Pre-generate the timeline segment id so the iframe is created under the
    // final segment key. The segment itself is still only committed to project
    // state after the iframe finishes loading.
    const timelineGfxId = this.generateEditId();

    const loadPromise = (async () => {
      await this.gfxManager.loadGfxForAddToTimeline(
        timelineGfxId,
        request.gfxInstanceId,
        request.gfxParams,
        { timeoutMs: ClippingStudioComponent.LOAD_TIMEOUT_MS },
      );

      const isSqz = this.gfxManager.isSqz(request.gfxInstanceId);

      if (isSqz) {
        const newInMs = this.manager.state.currentTimeMs;
        const newOutMs = newInMs + (request.durationMs ?? 10_000);

        const hasOverlap = this.effectiveGraphics
          .filter((g) => g.type === "gfx" && g.isSqz === true)
          .some((g) => newInMs < g.outMs && newOutMs > g.inMs);

        if (hasOverlap) {
          throw new Error("Only one SQZ GFX can be visible at a time. The selected interval overlaps with an existing SQZ segment.");
        }
      }

      this.manager.addGfxGraphic({
        id: timelineGfxId,
        gfxInstanceId: request.gfxInstanceId,
        gfxParams: request.gfxParams,
        durationMs: request.durationMs,
        isSqz: isSqz || undefined,
      });
    })().catch((err: Error) => {
      this.gfxManager.removeGfxInstance(timelineGfxId);
      throw err;
    });

    this._dialogService
      .openDialogWithLoading(
        this.viewContainerRef,
        {
          title: "Adding GFX",
          loadingContent: "Loading graphic...",
        },
        loadPromise,
        () => ({
          type: "success",
          title: "Done",
          content: "GFX added.",
          timeout: 3000,
          hideCloseButton: true,
        }),
        (err) => ({
          type: "error",
          title: "Error",
          content: err?.message ?? "GFX failed to load",
          hideCloseButton: false,
        }),
      )
      .finally(() => {
        this.isTimelineMediaLoading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Updates parameters of an existing timeline GFX segment.
   * Reuses the Add-to-timeline iframe-load contract (two-phase readiness +
   * timeout) under the same `timelineGfxId`. On success the project segment's
   * `gfxParams` are overwritten in place. On failure the segment is removed
   * and the user is instructed to re-add the GFX.
   */
  onUpdateTimelineGfxParams(request: UpdateTimelineGfxParamsRequest): void {
    if (this.isTimelineMediaLoading) return;
    if (this.state?.mode !== "EDIT") return;

    const finish = (success: boolean): void => {
      request.onComplete?.(success);
      this.cdr.markForCheck();
    };

    const isScaleKey = (key: string): boolean => key.trim().toLowerCase() === "scale";
    const paramsEqualIgnoringScale = (
      a: Record<string, string> | undefined,
      b: Record<string, string> | undefined,
    ): boolean => {
      const aKeys = Object.keys(a ?? {}).filter((k) => !isScaleKey(k));
      const bKeys = Object.keys(b ?? {}).filter((k) => !isScaleKey(k));
      if (aKeys.length !== bKeys.length) return false;
      const bKeySet = new Set(bKeys);
      for (const k of aKeys) {
        if (!bKeySet.has(k)) return false;
        const aHas = Object.prototype.hasOwnProperty.call(a ?? {}, k);
        const bHas = Object.prototype.hasOwnProperty.call(b ?? {}, k);
        if (aHas !== bHas) return false;
        if (!aHas || !bHas) continue;
        if ((a as Record<string, string>)[k] !== (b as Record<string, string>)[k]) return false;
      }
      return true;
    };

    const {
      timelineGfxId,
      gfxInstanceId,
      gfxParams,
      baselineGfxParams,
      baselineInMs,
      baselineOutMs,
    } = request;

    const durationMs = ClippingStudioComponent.resolveGfxDurationMs(gfxParams);
    const currentSeg = this.effectiveGraphics.find(
      (g) => g.id === timelineGfxId && g.type === "gfx",
    );
    const newInMs = currentSeg?.inMs ?? baselineInMs;
    let newOutMs = newInMs + durationMs;
    const maxTimelineMs = this.getEditTimelineDurationMs();
    if (maxTimelineMs > 0) {
      newOutMs = Math.min(newOutMs, maxTimelineMs);
    }

    const isSqz = this.gfxManager.isSqz(gfxInstanceId);
    if (isSqz) {
      const sqzOverlapMessage =
        "Only one SQZ GFX can be visible at a time. The selected interval overlaps with an existing SQZ segment.";
      const hasOverlap = this.effectiveGraphics
        .filter((g) => g.type === "gfx" && g.isSqz === true && g.id !== timelineGfxId)
        .some((g) => newInMs < g.outMs && newOutMs > g.inMs);

      if (hasOverlap) {
        this.manager.updateGraphic(
          timelineGfxId,
          {
            gfxParams: { ...baselineGfxParams },
            inMs: baselineInMs,
            outMs: baselineOutMs,
          },
          { skipUndo: true, skipPersist: true },
        );
        this.gfxManager.applyLiveScale(timelineGfxId, baselineGfxParams);
        finish(false);
        void this._dialogService
          .openDialog(this.viewContainerRef, {
            type: "error",
            title: "Error",
            content: sqzOverlapMessage,
            confirmButtonText: "OK",
            timeout: -1,
          })
          .finally(() => {});
        return;
      }
    }

    // Fast-path: no reload if built params match editor-open baseline (Scale excluded).
    // Live preview already wrote gfxParams into state; comparing to effective graphics
    // always matched and skipped reload — baseline comes from graphics-panel after merge.
    if (paramsEqualIgnoringScale(baselineGfxParams, gfxParams)) {
      this.manager.updateGraphic(timelineGfxId, {
        gfxParams,
        inMs: newInMs,
        outMs: newOutMs,
      });
      this.gfxManager.applyLiveScale(timelineGfxId, gfxParams);
      finish(true);
      return;
    }

    this.isTimelineMediaLoading = true;
    this.cdr.markForCheck();

    const loadPromise = (async () => {
      await this.gfxManager.loadGfxForAddToTimeline(
        timelineGfxId,
        gfxInstanceId,
        gfxParams,
        { timeoutMs: ClippingStudioComponent.LOAD_TIMEOUT_MS },
      );

      this.manager.updateGraphic(timelineGfxId, {
        gfxParams,
        inMs: newInMs,
        outMs: newOutMs,
      });
      finish(true);
    })().catch((err: Error) => {
      finish(false);
      this.gfxManager.removeGfxInstance(timelineGfxId);
      this.manager.removeGraphic(timelineGfxId);
      throw err;
    });

    this._dialogService
      .openDialogWithLoading(
        this.viewContainerRef,
        {
          title: "Updating GFX parameters",
          loadingContent: "Reloading graphic...",
        },
        loadPromise,
        () => ({
          type: "success",
          title: "Done",
          content: "GFX updated.",
          timeout: 3000,
          hideCloseButton: true,
        }),
        () => ({
          type: "error",
          title: "Error",
          content:
            "Failed to update GFX parameters. Please add the GFX again.",
          hideCloseButton: false,
        }),
      )
      .finally(() => {
        this.isTimelineMediaLoading = false;
        this.cdr.markForCheck();
      });
  }

  @HostListener("document:keydown", ["$event"])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (this.isTimelineMediaLoading || this.isPublishing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this.isTypingInInput(event)) return;
    if (this.showAutoReframeModal && this.isModalPlaybackOrFrameKey(event)) return;
    const handled = this.handleKeyboardShortcut(event);
    if (handled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  private isModalPlaybackOrFrameKey(event: KeyboardEvent): boolean {
    if (event.code === "Space" || event.code === "KeyK") return true;
    if (event.code === "ArrowLeft" || event.code === "ArrowRight") return true;
    if (event.code === "Delete" || event.code === "Backspace") return true;
    return false;
  }

  private isTypingInInput(event: KeyboardEvent): boolean {
    const t = event.target;
    return !!(
      t &&
      (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)
    );
  }

  private isSourceMode(): boolean {
    return this.state?.mode === "SOURCE";
  }

  private handleKeyboardShortcut(event: KeyboardEvent): boolean {
    const ctrlOrMeta = event.ctrlKey || event.metaKey;

    if (ctrlOrMeta) {
      if (event.shiftKey && event.code === "KeyZ") {
        this.manager.redoOperation();
        return true;
      }
      if (event.code === "KeyZ") {
        this.manager.undoOperation();
        return true;
      }
      if (event.code === "KeyS") {
        this.manager.saveProject();
        return true;
      }
      if (
        (event.shiftKey && event.code === "Slash") ||
        (event.altKey && event.code === "KeyK")
      ) {
        this.openKeyboardShortcutsDialog();
        return true;
      }
      return false;
    }

    // Shift+Backspace = reset playback speed (check first so no other handler sees Backspace)
    const isBackspace = event.code === "Backspace" || event.key === "Backspace";
    if (event.shiftKey && isBackspace) {
      this.playbackRate = 1;
      this.manager.setPlaybackRate(1);
      this.cdr.markForCheck();
      return true;
    }

    // Playback: K or Space = Play/Pause, M = Toggle audio
    if (event.code === "KeyK" || event.code === "Space") {
      if (this.state?.isPlaying) {
        this.manager.pause();
      } else {
        this.syncZoomedSegmentBoundsToManager();
        this.manager.play();
      }
      return true;
    }
    if (event.code === "KeyM") {
      this.audioMuted = !this.audioMuted;
      this.cdr.markForCheck();
      return true;
    }
    if (event.shiftKey && event.code === "ArrowUp") {
      this.playbackRate = Math.min(
        3,
        Math.round((this.playbackRate + 0.1) * 10) / 10,
      );
      this.manager.setPlaybackRate(this.playbackRate);
      this.cdr.markForCheck();
      return true;
    }
    if (event.shiftKey && event.code === "ArrowDown") {
      this.playbackRate = Math.max(
        0.25,
        Math.round((this.playbackRate - 0.1) * 10) / 10,
      );
      this.manager.setPlaybackRate(this.playbackRate);
      this.cdr.markForCheck();
      return true;
    }

    // Navigation: Arrow keys, Shift+I/O
    if (event.code === "ArrowLeft") {
      this.onFrameDown();
      return true;
    }
    if (event.code === "ArrowRight") {
      this.syncZoomedSegmentBoundsToManager();
      this.manager.frameUp();
      return true;
    }
    if (this.isSourceMode()) {
      if (event.shiftKey && event.code === "KeyI") {
        this.onGoToIn();
        return true;
      }
      if (event.shiftKey && event.code === "KeyO") {
        this.onGoToOut();
        return true;
      }

      // Markers: I = Mark IN, O = Mark OUT (no Shift)
      if (!event.shiftKey && event.code === "KeyI") {
        this.manager.markIn();
        return true;
      }
      if (!event.shiftKey && event.code === "KeyO") {
        this.manager.markOut();
        return true;
      }
    }

    // Edit: Escape = Unselect, Delete = Delete selected segment (text/graphic first, then clip/marker)
    if (event.code === "Escape") {
      this.manager.selectClip(null);
      this.manager.selectMarker(null);
      this.manager.selectText(null);
      this.manager.selectGraphic(null);
      return true;
    }
    if (event.code === "Delete") {
      // Prefer deleting the selected text or graphic segment when one is selected
      if (this.state?.selectedTextId) {
        this.manager.removeText(this.state.selectedTextId);
        this.manager.selectText(null);
        return true;
      }
      if (this.state?.selectedGraphicId) {
        this.manager.removeGraphic(this.state.selectedGraphicId);
        this.manager.selectGraphic(null);
        return true;
      }
      if (this.state?.selectedClipId) {
        this.manager.removeClip(this.state.selectedClipId);
        return true;
      }
      if (this.state?.selectedMarkerId) {
        const marker = this.state.markers?.find(
          (m) => m.id === this.state?.selectedMarkerId,
        );
        if (marker?.appendedSegmentId) {
          this.manager.removeAppendedMamSegmentByMarkerId(
            this.state.selectedMarkerId,
          );
        } else {
          this.manager.removeMarker(this.state.selectedMarkerId);
        }
        return true;
      }
      return false;
    }

    // Sequence: + - Zoom in, - - Zoom out, Shift+1 - Zoom to fit, Shift+2 - Zoom to segment
    if (
      event.code === "NumpadAdd" ||
      (event.shiftKey && event.code === "Equal")
    ) {
      this.timelineZoom(-1);
      return true;
    }
    if (event.code === "Minus" || event.code === "NumpadSubtract") {
      this.timelineZoom(1);
      return true;
    }
    if (event.shiftKey && event.code === "Digit1") {
      this.timelineZoomFitVideo();
      return true;
    }
    if (event.shiftKey && event.code === "Digit2") {
      this.timelineZoomFitSegment();
      return true;
    }

    return false;
  }

  onPlay(): void {
    this.syncZoomedSegmentBoundsToManager();
    this.manager.play();
  }

  onPause(): void {
    this.manager.pause();
  }

  onStop(): void {
    this.manager.stop();
  }

  onGoToIn(): void {
    this.videoPreviewRef?.clearSqzInsetCache();
    if (
      this.state?.mode === "SOURCE" &&
      (this.state?.appendedMamSegments?.length ?? 0) > 0 &&
      this.zoomedSourceSegmentIndex != null
    ) {
      this.manager.seek(this.getSegmentStartMs(this.zoomedSourceSegmentIndex));
    } else {
      this.manager.goToIn();
    }
    setTimeout(() => this.timelineRef?.translateTimeline("afterZoom"), 0);
  }

  onGoToOut(): void {
    if (
      this.state?.mode === "SOURCE" &&
      (this.state?.appendedMamSegments?.length ?? 0) > 0 &&
      this.zoomedSourceSegmentIndex != null
    ) {
      this.manager.seek(this.getSegmentEndMs(this.zoomedSourceSegmentIndex));
    } else {
      this.manager.goToOut();
    }
    setTimeout(() => this.timelineRef?.translateTimeline("afterZoom"), 0);
  }

  onFrameDown(): void {
    this.videoPreviewRef?.clearSqzInsetCache();
    this.syncZoomedSegmentBoundsToManager();
    this.manager.frameDown();
  }

  onFrameUp(): void {
    this.syncZoomedSegmentBoundsToManager();
    this.manager.frameUp();
  }

  onMarkIn(): void {
    this.manager.markIn();
  }

  onMarkOut(): void {
    this.manager.markOut();
  }

  onSeek(timeMs: number): void {
    this.videoPreviewRef?.clearSqzInsetCache();
    this.manager.seek(timeMs);
  }

  onClipReordered(orderedIds: string[]): void {
    this.manager.reorderClips(orderedIds);
  }

  formatTime(timeMs: number): string {
    return this.manager.formatTime(timeMs);
  }

  onJumpToLive(): void {
    this.manager.jumpToLive();
  }

  onJumpBackMinutes(minutes: number): void {
    this.videoPreviewRef?.clearSqzInsetCache();
    this.manager.jumpBackMinutes(minutes);
  }

  toggleRewindMenu(): void {
    this.rewindMenuOpen = !this.rewindMenuOpen;
  }

  closeRewindMenu(): void {
    this.rewindMenuOpen = false;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.rewindMenuOpen &&
      this.rewindWrapRef?.nativeElement &&
      !this.rewindWrapRef.nativeElement.contains(event.target as Node)
    ) {
      this.closeRewindMenu();
    }
  }

  async onSnapshot(req?: {
    aspectRatio?: ClipPreviewAspectRatio;
    destinationUid?: string | null;
  }): Promise<void> {
    if (this.manager.isCapturingClipPreview) return;

    const snapshotTimeMs =
      this.state?.mode === "EDIT" && (this.state.clips?.length ?? 0) > 0
        ? this.manager.getCurrentSourceTimeMs()
        : (this.state?.currentTimeMs ?? this.manager.getCurrentSourceTimeMs());
    const snapshotFullEditMs =
      this.state?.mode === "EDIT" && (this.state.clips?.length ?? 0) > 0
        ? this.roundMilliseconds(this.state.currentTimeMs)
        : null;
    const aspectRatio =
      req?.aspectRatio ?? this.toClipPreviewAspectRatio(this.previewAspectRatio);
    const destinationUid = req?.destinationUid ?? null;

    this.manager.setCapturingClipPreview(true);
    this.cdr.detectChanges();
    try {
      const dataUrl = await this.captureClipPreviewImage({
        sourceTimeMs: snapshotTimeMs,
        overlayTimeMs: snapshotFullEditMs,
        aspectRatio,
        destinationUid,
      });
      if (!dataUrl) {
        this.toastr.error("Could not capture snapshot. Please try again.");
        return;
      }
      await this.publishState.applyClipPreviewSnapshot({
        previewBase64: dataUrl,
        timeMs: snapshotTimeMs,
        fullEditMs: snapshotFullEditMs,
        aspectRatio,
        destinationUid,
      });
    } catch (err) {
      console.error("clipping-studio->onSnapshot", err);
      this.toastr.error("Could not capture snapshot. Please try again.");
    } finally {
      this.manager.setCapturingClipPreview(false);
      this.cdr.detectChanges();
    }
  }

  /**
   * Frame provider for editor reopen / resolveVideoSnapshotEditorSource.
   * Uses the same SOURCE raw / EDIT composed path as Take Snapshot.
   */
  private async captureClipPreviewFrameForProvider(
    timeMs: number,
    options?: {
      overlayTimeMs?: number | null;
      destinationUid?: string | null;
      aspectRatio?: ClipPreviewAspectRatio;
    },
  ): Promise<string | null> {
    const aspectRatio =
      options?.aspectRatio ?? this.toClipPreviewAspectRatio(this.previewAspectRatio);
    return this.captureClipPreviewImage({
      sourceTimeMs: timeMs,
      overlayTimeMs: options?.overlayTimeMs ?? null,
      aspectRatio,
      destinationUid: options?.destinationUid ?? this.selectedPreviewDestinationId,
    });
  }

  /** SOURCE: bare TranscodedFile frame. EDIT: WYSIWYG compose from TranscodedFile base. */
  private async captureClipPreviewImage(args: {
    sourceTimeMs: number;
    overlayTimeMs?: number | null;
    aspectRatio: ClipPreviewAspectRatio;
    destinationUid?: string | null;
  }): Promise<string | null> {
    const state = this.state;
    if (!state?.media?.id) return null;

    const isEditMode = state.mode === "EDIT";
    const resolved = resolveTranscodedSourceAtTime({
      timeMs: args.sourceTimeMs,
      mediaId: state.media.id,
      mainDurationMs: state.media.durationMs ?? 0,
      appendedMamSegments: state.appendedMamSegments ?? [],
      isEditMode,
      editModeClips: isEditMode ? (state.clips ?? []) : [],
    });
    if (!resolved) return null;

    if (!isEditMode) {
      return captureTranscodedFrameDataUrl({
        getTranscodedUrl: (id) => this.manager.getMediaTranscodedUrl(id),
        mediaId: resolved.mediaId,
        timeSec: resolved.timeSec,
      });
    }

    const reframeConfig = this.getReframeConfigAtSourceTime(
      this.selectedPreviewDestinationId,
      args.sourceTimeMs,
    );
    const composeAspect = this.toClipPreviewAspectRatio(this.previewAspectRatio);
    const url = await this.manager.getMediaTranscodedUrl(resolved.mediaId);
    return withEphemeralTranscodedVideo(url, resolved.timeSec, async (video) => {
      const preview = this.videoPreviewRef;
      if (!preview) return null;
      return preview.captureComposedClipPreviewFromVideo({
        video,
        aspectRatio: composeAspect,
        reframeConfig,
        maxHeight: CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
        jpegQuality: CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
        overlayTimeMs: args.overlayTimeMs ?? null,
      });
    });
  }

  /** Reframe at source-timeline time for the preview (or given) destination — matches live preview when at playhead. */
  private getReframeConfigAtSourceTime(
    destinationUid: string | null | undefined,
    sourceTimeMs: number,
  ): ReframeConfig | null {
    if (!destinationUid || this.state?.mode !== "EDIT") return null;
    const variants = this.state.destinationVariants?.[destinationUid];
    if (!variants?.segmentReframes) return null;
    const ordered = (this.state.clips ?? []).slice().sort((a, b) => a.order - b.order);
    const clipAt = ordered.find(
      (c) => sourceTimeMs >= c.inMs && sourceTimeMs < c.outMs,
    );
    if (!clipAt) return null;
    const keyframes = variants.segmentReframes[clipAt.id] ?? [];
    return getReframeConfigAtTime(keyframes, sourceTimeMs - clipAt.inMs);
  }

  private roundMilliseconds(value: number | null | undefined): number | null {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
      return null;
    }

    return Math.round(value);
  }

  onCut(): void {
    const clipId = this.manager.addSegmentFromInOut();
    if (clipId) this.captureThumbnailsForSegment(clipId, 0);
  }

  /** Queue of segment ids waiting for thumbnail capture (from panel). Processed one-by-one to avoid seek races. */
  private segmentAddedThumbnailQueue: string[] = [];
  private segmentAddedThumbnailProcessing = false;

  /** Delay (ms) before starting thumbnail capture so the video element is ready after panel seeks. */
  private static readonly SEGMENT_ADDED_THUMBNAIL_DELAY_MS = 250;
  /** Retry delay (ms) when capture returns null (video not ready). */
  private static readonly SEGMENT_ADDED_THUMBNAIL_RETRY_DELAY_MS = 300;
  private static readonly SEGMENT_ADDED_THUMBNAIL_MAX_RETRIES = 3;

  onSegmentAddedFromPanel(clipId: string): void {
    this.segmentAddedThumbnailQueue.push(clipId);
    setTimeout(() => {
      this.ngZone.run(() => {
        this.cdr.detectChanges();
        this.processSegmentAddedThumbnailQueue();
      });
    }, ClippingStudioComponent.SEGMENT_ADDED_THUMBNAIL_DELAY_MS);
  }

  private processSegmentAddedThumbnailQueue(): void {
    if (
      this.segmentAddedThumbnailProcessing ||
      this.segmentAddedThumbnailQueue.length === 0
    )
      return;
    const clipId = this.segmentAddedThumbnailQueue.shift()!;
    this.segmentAddedThumbnailProcessing = true;
    this.captureThumbnailsForSegment(clipId, 0, true)
      .then(() => {
        this.segmentAddedThumbnailProcessing = false;
        this.processSegmentAddedThumbnailQueue();
      })
      .catch(() => {
        this.segmentAddedThumbnailProcessing = false;
        this.processSegmentAddedThumbnailQueue();
      });
  }

  private captureThumbnailsForSegment(
    clipId: string,
    retryCount: number = 0,
    useHiddenVideo: boolean = false,
  ): Promise<void> {
    const clip = this.manager.state.clips.find((c) => c.id === clipId);
    if (!clip || !this.videoPreviewRef) return Promise.resolve();
    const fps = this.manager.state.media?.fps ?? 30;
    const oneFrameMs = 1000 / fps;
    const lastFrameTimeMs = Math.max(clip.inMs, clip.outMs - oneFrameMs);
    const captureOpts = useHiddenVideo ? { useHiddenVideo: true } : undefined;
    return this.videoPreviewRef
      .captureSnapshotAtTime(clip.inMs, captureOpts)
      .then((firstUrl) =>
        this.videoPreviewRef!.captureSnapshotAtTime(lastFrameTimeMs, captureOpts).then((lastUrl) => ({
          firstUrl,
          lastUrl,
        })),
      )
      .then(({ firstUrl, lastUrl }) => {
        if (firstUrl && lastUrl) {
          this.ngZone.run(() => {
            this.manager.setClipThumbnails(clipId, firstUrl, lastUrl);
            this.cdr.detectChanges();
          });
          return;
        }
        if (
          retryCount < ClippingStudioComponent.SEGMENT_ADDED_THUMBNAIL_MAX_RETRIES
        ) {
          return new Promise<void>((resolve) =>
            setTimeout(() => {
              this.captureThumbnailsForSegment(clipId, retryCount + 1, useHiddenVideo)
                .then(resolve)
                .catch(resolve);
            }, ClippingStudioComponent.SEGMENT_ADDED_THUMBNAIL_RETRY_DELAY_MS),
          );
        }
      })
      .catch(() => {
        // Ignore capture failures (e.g. seek timeout); retry on next attempt
        if (
          retryCount < ClippingStudioComponent.SEGMENT_ADDED_THUMBNAIL_MAX_RETRIES
        ) {
          return new Promise<void>((resolve) =>
            setTimeout(() => {
              this.captureThumbnailsForSegment(clipId, retryCount + 1, useHiddenVideo)
                .then(resolve)
                .catch(resolve);
            }, ClippingStudioComponent.SEGMENT_ADDED_THUMBNAIL_RETRY_DELAY_MS),
          );
        }
      });
  }

  onAddMarker(): void {
    this.manager.addMarkerFromIn();
  }

  onMarkerTextChange(event: { markerId: string; text: string }): void {
    this.manager.updateMarkerText(event.markerId, event.text);
  }

  onToggleAnnotations(): void {
    this.showAnnotationToolbar = !this.showAnnotationToolbar;
  }

  toggleCaptions(): void {
    this.captionsOn = !this.captionsOn;
  }

  onAudioMutedChange(muted: boolean): void {
    this.audioMuted = muted;
  }

  onMainAudioSelectionChange(mediaId: number | null): void {
    void this.manager.setMainAudioSelection(mediaId);
  }

  onLeftSidebarClose(): void {
    this.showLeftSidebar = false;
  }

  onLeftSidebarOpen(): void {
    this.showLeftSidebar = true;
  }

  onRightSidebarClose(): void {
    this.showRightSidebar = false;
  }

  onRightSidebarOpen(): void {
    this.showRightSidebar = true;
  }

  onPublishContinue(): void {
    this.runPublishFlow(true);
  }

  onPublishExit(): void {
    this.runPublishFlow(false);
  }

  /**
   * Saves the project then submits it for publication. Shows a blocking modal (GFX add-to-timeline pattern).
   * On Exit (cloneProject=false) success, navigates to the publications list; Continue stays in the editor.
   */
  private runPublishFlow(cloneProject: boolean): void {
    if (this.isPublishing) return;

    if (
      !this.getPublicationUidParam() ||
      this.manager.state.projectId == null ||
      this.manager.state.projectId <= 0 ||
      this.publishState.selectedDestinationIds.length === 0
    ) {
      void this._dialogService.openDialog(this.viewContainerRef, {
        type: "error",
        title: "Error",
        content: "Cannot publish: project not loaded or no destination selected.",
        confirmButtonText: "OK",
      });
      return;
    }

    const gateError = this.publishState.publishDisabledReason();
    if (!this.publishState.canPublish() && gateError) {
      this.publishState.setPreSubmitError(gateError);
      this.cdr.markForCheck();
      return;
    }

    this.publishState.setPreSubmitError(null);

    const durationError = this.publishState.validateTikTokEditDuration(
      this.manager.getEditTimelineDurationMs(),
    );
    if (durationError) {
      this.toastr.error(durationError, "Cannot publish", { timeOut: 5000 });
      return;
    }

    this.isPublishing = true;
    this.cdr.markForCheck();

    let publishSucceeded = false;
    const loadPromise = (async () => {
      await this.manager.saveProject();
      await this.manager.submitProjectForPublication(cloneProject);
      publishSucceeded = true;
    })();

    this._dialogService
      .openDialogWithLoading(
        this.viewContainerRef,
        {
          title: "Publishing",
          loadingContent: "Saving project and starting publish...",
        },
        loadPromise,
        () => ({
          type: "success",
          title: "Done",
          content: "Publication submitted.",
          timeout: 3000,
          hideCloseButton: true,
        }),
        (err) => {
          console.error("Publish failed", err);
          return {
            type: "error",
            title: "Error",
            content: "Publish failed. Please try again.",
            hideCloseButton: false,
          };
        },
      )
      .then(({ result }) => {
        if (publishSucceeded && result && !cloneProject) {
          void this.router.navigateByUrl("/clipping-studio");
        }
      })
      .finally(() => {
        this.isPublishing = false;
        this.cdr.markForCheck();
      });
  }

  /** Get publicationUid from current route or any child (handles nested route outlets). */
  private getPublicationUidParam(): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const uid = route.snapshot.paramMap.get("publicationUid");
      if (uid) return uid;
      route = route.firstChild;
    }
    return null;
  }

  private generateEditId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return "e" + Date.now() + Math.random().toString(36).slice(2, 11);
  }

  private toClipPreviewAspectRatio(ratio: AspectRatio): ClipPreviewAspectRatio {
    return ratio === AspectRatio.Aspect9x16 ||
      ratio === AspectRatio.Aspect1x1 ||
      ratio === AspectRatio.Aspect4x3
      ? ratio
      : AspectRatio.Aspect16x9;
  }

  timelineZoom(change: number): void {
    if (this.isFitSegment) return;
    const isSourceWithMam =
      this.state?.mode === "SOURCE" &&
      (this.state?.appendedMamSegments?.length ?? 0) > 0;

    if (change < 0) {
      if (
        isSourceWithMam &&
        this.zoomedSourceSegmentIndex == null &&
        this.isVideoTimelineFit
      ) {
        this.zoomedSourceSegmentIndex = this.getSegmentIndexAtPlayhead();
        this.zoomedSegmentFit = true;
        const effectiveSec = this.getEffectiveTimelineDurationSec(this.state);
        if (effectiveSec > 0) this.calculateZoomLevels(effectiveSec);
        this.cdr.markForCheck();
        setTimeout(() => {
          if (this.timelineRef) {
            this.timelineRef.onChangeEffectingTimeline(true);
          }
        }, 0);
        return;
      }
      if (
        isSourceWithMam &&
        this.zoomedSourceSegmentIndex != null &&
        this.zoomedSegmentFit
      ) {
        this.zoomedSegmentFit = false;
        if (this.timelineZoomSteps.length >= 2) {
          this.selectedTimelineZoomStepIndex = this.timelineZoomSteps.length - 2;
          this.selectedTimelineBlockDuration =
            this.timelineZoomSteps[this.selectedTimelineZoomStepIndex];
        }
        this.cdr.markForCheck();
        setTimeout(() => {
          if (this.timelineRef) {
            this.timelineRef.onChangeEffectingTimeline();
          }
        }, 0);
        return;
      }
    }

    if (change > 0) {
      if (
        isSourceWithMam &&
        this.zoomedSourceSegmentIndex != null &&
        this.zoomedSegmentFit
      ) {
        this.zoomedSourceSegmentIndex = null;
        this.zoomedSegmentFit = false;
        const effectiveSec = this.getEffectiveTimelineDurationSec(this.state);
        if (effectiveSec > 0) this.calculateZoomLevels(effectiveSec);
        this.selectedTimelineZoomStepIndex = this.timelineZoomSteps.length - 1;
        this.selectedTimelineBlockDuration =
          this.timelineZoomSteps[this.selectedTimelineZoomStepIndex];
        this.cdr.markForCheck();
        setTimeout(() => {
          if (this.timelineRef) {
            this.timelineRef.onChangeEffectingTimeline(true);
          }
        }, 0);
        return;
      }
    }

    const newIndex = this.selectedTimelineZoomStepIndex + change;
    if (newIndex >= 0 && newIndex <= this.timelineZoomSteps.length - 1) {
      this.selectedTimelineZoomStepIndex = newIndex;
      this.selectedTimelineBlockDuration =
        this.timelineZoomSteps[this.selectedTimelineZoomStepIndex];
      if (
        isSourceWithMam &&
        this.zoomedSourceSegmentIndex != null &&
        !this.zoomedSegmentFit &&
        newIndex === this.timelineZoomSteps.length - 1
      ) {
        this.zoomedSegmentFit = true;
      }
      this.cdr.markForCheck();
      setTimeout(() => {
        if (this.timelineRef) {
          const recalcWidth =
            !isSourceWithMam || this.zoomedSourceSegmentIndex == null;
          this.timelineRef.onChangeEffectingTimeline(recalcWidth);
        }
      }, 0);
    }
  }

  timelineZoomFitVideo(): void {
    this.isFitSegment = false;
    this.zoomedSourceSegmentIndex = null;
    this.zoomedSegmentFit = false;
    const effectiveSec = this.getEffectiveTimelineDurationSec(this.state);
    if (effectiveSec > 0) {
      this.calculateZoomLevels(effectiveSec);
    }
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.timelineRef) {
        this.timelineRef.onChangeEffectingTimeline();
      }
    }, 0);
  }

  timelineZoomFitSegment(): void {
    this.isFitSegment = true;
    const effectiveSec = this.getEffectiveTimelineDurationSec(this.state);
    if (effectiveSec > 0) {
      this.calculateZoomLevels(effectiveSec);
    }
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.timelineRef) {
        this.timelineRef.onChangeEffectingTimeline();
      }
    }, 0);
  }

  private calculateZoomLevels(timeSec: number): void {
    this.selectedTimelineBlockDuration = this.roundDown(timeSec / 6);
    if (this.isFitSegment) return;
    this.timelineZoomSteps = this.availableTimelineZoomSteps.filter(
      (z) => z < this.selectedTimelineBlockDuration,
    );
    this.timelineZoomSteps.push(this.selectedTimelineBlockDuration);
    this.selectedTimelineZoomStepIndex = this.timelineZoomSteps.length - 1;
  }

  private roundDown(num: number): number {
    if (num < 10) return num;
    if (num < 100) return Math.floor(num / 10) * 10;
    if (num < 1000) return Math.floor(num / 100) * 100;
    if (num < 10000) return Math.floor(num / 1000) * 1000;
    if (num < 100000) return Math.floor(num / 10000) * 10000;
    return num;
  }
  calculateTitleWidth(): string {
    return this.editedTitle.length * 11 + "px";
  }
}


